import { useState } from "react";
import shortid from "shortid";
import * as XLSX from "xlsx";
import { Loading, Step, StepLabel, Stepper } from "../../components";
import { InputErros } from "../../helpers";
import { Minimal } from "../../layouts";
import {
  ClassificacoesServicoNacionalService,
  CodigosServicosNacionalService,
  notification,
  ServicoService,
} from "../../services";
import { responseErros } from "../../utils";
import { ImportarServicoActions, ImportarServicoStepper } from "./components";
import servicosCsvValidator from "./middlewares/servicos-csv.validator";
import { Servico } from "../servicos/add-servicos/entities/servico.entity";

const codigosServicosNacionalService = new CodigosServicosNacionalService();
const classificacoesServicoNacionalService =
  new ClassificacoesServicoNacionalService();

const ImportarServicoView = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [arquivoCsv, setArquivoCsv] = useState(null);
  const [dadosCsv, setDadosCsv] = useState(null);
  const [isCadastro, setIsCadastro] = useState(false);
  const [codigosServicoNacionalList, setCodigosServicoNacionalList] = useState(
    [],
  );
  const [
    classificacoesServicoNacionalList,
    setClassificacoesServicoNacionalList,
  ] = useState([]);

  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const servicoService = new ServicoService();

  const steps = [
    { titulo: "Selecionar Arquivo", funcaoAcao: buscarXmlData },
    { titulo: "Serviços", funcaoAcao: cadastrarServicos },
  ];

  function extrairDadosServicos(csvData) {
    const workbook = XLSX.read(csvData, { type: "buffer" });
    let dataServicos = [];
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(worksheet);
    data.forEach((item) => {
      dataServicos.push({
        ...item,
        id: shortid.generate(),
        ativado: true,
      });
    });
    return dataServicos?.length ? dataServicos : [];
  }

  const handleFileRead = async (e) => {
    const csvData = e.target.result;
    const jsonData = extrairDadosServicos(csvData);
    await formatarServicos(jsonData);
    setIsCadastro(true);
  };

  async function buscarXmlData() {
    try {
      if (!arquivoCsv) {
        throw new Error(
          "Por favor, inserir CSV/XLS referente a importação de produtos",
        );
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async (e) => {
          try {
            await handleFileRead(e);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () =>
          reject(new Error("Erro ao ler o arquivo CSV/XLS"));
        reader.readAsArrayBuffer(arquivoCsv);
      });
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async function cadastrarServicos() {
    if (!dadosCsv?.length) {
      throw new Error(
        "Não há dados para importar. Por favor, verifique o arquivo CSV/XLS",
      );
    }
    servicosCsvValidator
      .validate(dadosCsv, { abortEarly: false })
      .then(async () => {
        setInputErro([]);
        setLoading(true);
        const res = await servicoService.cadastrarListaServicos(dadosCsv);
        setLoading(false);
        if (!res.isAxiosError) {
          notification.sucessoGenericos("Serviços importados com sucesso");
          handleInitStep();
        } else responseErros(res);
        setLoading(false);
      })
      .catch((err) => {
        inputErros.set(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  async function formatarServicos(dados) {
    setLoading(true);
    const [servicos, codigosServicoNacional, classificacoesServicoNacional] = [
      [],
      [],
      [],
    ];
    dados.forEach((item) => {
      servicos.push({
        ...new Servico(item),
        id: shortid.generate(),
      });
      if (item.estCodigoServicoNacionalId) {
        codigosServicoNacional.push(item.estCodigoServicoNacionalId);
      }
      if (item.estClassificacaoServicoNacionalId) {
        classificacoesServicoNacional.push(
          item.estClassificacaoServicoNacionalId,
        );
      }
    });
    setDadosCsv(servicos);
    if (codigosServicoNacional.length) {
      buscarCodigosServicoNacional({
        id: [...new Set(codigosServicoNacional)],
      });
    }
    if (classificacoesServicoNacional.length) {
      buscarClassificacoesServicoNacional({
        id: [...new Set(classificacoesServicoNacional)],
      });
    }
  }

  function buscarCodigosServicoNacional(filtros) {
    codigosServicosNacionalService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setCodigosServicoNacionalList(result.data);
      } else {
        responseErros(result);
      }
    });
  }

  const buscarClassificacoesServicoNacional = async (filtros) => {
    classificacoesServicoNacionalService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setClassificacoesServicoNacionalList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const handleInitStep = () => {
    setActiveStep(0);
    setArquivoCsv(null);
    setDadosCsv(null);
    setIsCadastro(false);
    setInputErro([]);
  };

  return (
    <>
      <Minimal
        title="Importação de serviços"
        cardAction={
          <ImportarServicoActions
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handleInitStep={handleInitStep}
            isCadastro={isCadastro}
            setIsCadastro={setIsCadastro}
            setLoading={setLoading}
          />
        }
      >
        <Stepper activeStep={activeStep}>
          {steps.map(({ titulo }) => (
            <Step key={titulo}>
              <StepLabel>{titulo}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <ImportarServicoStepper
          activeStep={activeStep}
          arquivoCsv={arquivoCsv}
          setArquivoCsv={setArquivoCsv}
          dadosCsv={dadosCsv}
          setIsCadastro={setIsCadastro}
          codigosServicoNacionalList={codigosServicoNacionalList}
          classificacoesServicoNacionalList={classificacoesServicoNacionalList}
          inputErros={inputErros}
        />
      </Minimal>
      <Loading loading={loading} />
    </>
  );
};

export default ImportarServicoView;
